import * as React from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import ConfirmDialog from "../../../components/shared/ConfirmDialog";
import { useToast } from "../../../components/shared/ToastProvider";
import SecurityExpenseFormModal from "./SecurityExpenseFormModal";

import "../../../styles/main.css";
import "../../../styles/pages/condoUnitDetail.css";

// UI-only modal for updating/refunding a security deposit.
export default function SecurityDepositRefundModal({
  open,
  onClose,
  initialValues = {}
}) {
  const { showToast } = useToast();

  // Local UI-only state for expenses modal and delete
  const [isExpenseModalOpen, setIsExpenseModalOpen] = React.useState(false);
  const [expenseModalMode, setExpenseModalMode] = React.useState("add"); // "add" | "edit"
  const [selectedExpense, setSelectedExpense] = React.useState(null);
  const [isExpenseDeleteOpen, setIsExpenseDeleteOpen] = React.useState(false);
  const [expenseToDelete, setExpenseToDelete] = React.useState(null);

  const handleUpdate = () => {
    if (showToast) {
      showToast({ message: "Security deposit updated", severity: "success" });
    }
    if (onClose) onClose();
  };

  const handleOpenExpenseAdd = () => {
    setExpenseModalMode("add");
    setSelectedExpense(null);
    setIsExpenseModalOpen(true);
  };

  const handleOpenExpenseEdit = (row) => {
    setExpenseModalMode("edit");
    setSelectedExpense(row);
    setIsExpenseModalOpen(true);
  };

  const handleCloseExpenseModal = () => {
    setIsExpenseModalOpen(false);
    setSelectedExpense(null);
  };

  const handleOpenExpenseDelete = (row) => {
    setExpenseToDelete(row);
    setIsExpenseDeleteOpen(true);
  };

  const handleConfirmExpenseDelete = () => {
    setIsExpenseDeleteOpen(false);
    setExpenseToDelete(null);
    if (showToast) {
      showToast({ message: "Expense deleted", severity: "success" });
    }
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handleUpdate}>
        Update
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update Security Deposit"
      actions={actions}
      maxWidth="sm"
      fullWidth
    >
      <Box className="modal-form-grid modal-form-grid--spaced">
        {/* Returned Amount / Returned Date */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            required
            size="small"
            label="Returned Amount"
            type="number"
            defaultValue={initialValues.returnedAmountValue || ""}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Returned Date"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.returnedDateRaw || ""}
          />
        </Box>

        {/* Security Expenses Breakdown table */}
        <Box className="modal-form-row" mt={1}>
          <Typography
            variant="subtitle2"
            className="modal-section-title"
          >
            Security Expenses Breakdown
          </Typography>

          <TableContainer className="condo-unit-detail-table-container">
            <Table size="small" className="condo-unit-detail-table">
              <TableHead className="condo-unit-detail-table-head">
                <TableRow>
                  <TableCell className="modal-table-actions-cell" />
                  <TableCell>Item</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Static UI-only row for now; backend will control list later */}
                <TableRow>
                  <TableCell className="modal-table-actions-cell">
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        handleOpenExpenseEdit({ item: "Misc", currency: "PHP", amountValue: "120000.00" })
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() =>
                        handleOpenExpenseDelete({ item: "Misc", currency: "PHP", amountValue: "120000.00" })
                      }
                    >
                      Delete
                    </Button>
                  </TableCell>
                  <TableCell>Misc</TableCell>
                  <TableCell>PHP</TableCell>
                  <TableCell className="numeric-cell">120,000.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={1} display="flex" justifyContent="flex-end">
            <Button variant="contained" size="small" onClick={handleOpenExpenseAdd}>
              + Add Expense(s)
            </Button>
          </Box>
        </Box>
      </Box>
      <SecurityExpenseFormModal
        open={isExpenseModalOpen}
        onClose={handleCloseExpenseModal}
        mode={expenseModalMode}
        initialValues={selectedExpense || {}}
      />

      <ConfirmDialog
        open={isExpenseDeleteOpen}
        onClose={() => setIsExpenseDeleteOpen(false)}
        onConfirm={handleConfirmExpenseDelete}
        title="Delete Record"
        message="Are you sure you want to delete this expense? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </Modal>
  );
}
